/**
 * Student-facing payment-plan helpers.
 *
 * Plans are serviced by The Payment Option (TPO), a separate company, and
 * the app deliberately shows only a coarse status plus the figures already
 * on the plan row (paid / total / monthly). Nothing here computes balances,
 * schedules, or reads TPO-internal fields — those live at
 * app.thepaymentoption.com.
 */

// The one-line caption every plan surface carries (owner-approved wording)
export const TPO_CAPTION = 'Serviced by The Payment Option (TPO)';
export const TPO_URL = 'https://app.thepaymentoption.com';

export function tpoAgreementUrl(plan) {
    return plan && plan.tpo_onboard_hash ? `${TPO_URL}/#/sign-agreement/${plan.tpo_onboard_hash}` : null;
}

function num(value) {
    const n = parseFloat(value);
    return Number.isNaN(n) ? 0 : n;
}

export function isPaidOff(plan) {
    return num(plan.total) > 0 && num(plan.paid) >= num(plan.total);
}

// First payment still ahead: defer_on is a DATE column, compared by calendar day
export function isDeferred(plan) {
    if (!plan.defer_on) return false;
    const start = String(plan.defer_on).slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return start > today;
}

/**
 * Coarse status for a chip. Order matters: onboarding states first (the
 * plan isn't live yet), then paid off, paused, then deferred vs active.
 * Returns {key, label, type} where type is a StatusChip variant.
 */
export function planStatus(plan) {
    if (plan.cancelled_at)
        return {key: 'cancelled', label: 'Cancelled', type: 'neutral'};
    if (plan.is_tpo && !plan.is_tpo_onboarded && !plan.is_tpo_complete)
        return {key: 'onboarding', label: 'Setup needed', type: 'warning'};
    if (plan.is_tpo && plan.is_tpo_onboarded && !plan.is_tpo_complete)
        return {key: 'agreement_pending', label: 'Agreement pending', type: 'warning'};
    if (isPaidOff(plan))
        return {key: 'paid_off', label: 'Paid off', type: 'success'};
    if (!plan.active)
        return {key: 'paused', label: 'Paused', type: 'neutral'};
    if (isDeferred(plan))
        return {key: 'deferred', label: 'Deferred', type: 'info'};
    return {key: 'active', label: 'Active', type: 'info'};
}

// --- Alert predicates (moved verbatim from Dashboard.vue) ---------------

// Cancelled plans never drive an alert (nothing to sign, collect, or add)
function live(plans) {
    return (plans || []).filter(plan => !plan.cancelled_at);
}

export function needsOnboarding(plans) {
    // They haven't started onboarding yet - need link
    return live(plans).some(plan =>
        plan.is_tpo === true &&
        plan.is_tpo_onboarded === false &&
        plan.is_tpo_complete === false
    );
}

export function needsDocuSign(plans) {
    // They've onboarded but haven't completed the agreement signing
    return live(plans).some(plan =>
        plan.is_tpo === true &&
        plan.is_tpo_onboarded === true &&
        plan.is_tpo_complete === false
    );
}

export function tpoOnboardingUrl(plans) {
    const nonOnboardedPlan = live(plans).find(plan =>
        plan.is_tpo === true &&
        plan.is_tpo_onboarded === false &&
        plan.tpo_onboard_hash
    );
    return nonOnboardedPlan ? tpoAgreementUrl(nonOnboardedPlan) : '';
}

export function tpoPendingAgreementUrl(plans) {
    const pendingPlan = live(plans).find(plan =>
        plan.is_tpo === true &&
        plan.is_tpo_complete === false &&
        plan.tpo_onboard_hash
    );
    return pendingPlan ? tpoAgreementUrl(pendingPlan) : '';
}

export function hasUnpaidSalesTax(plans) {
    return live(plans).some(plan =>
        plan.is_tpo_complete &&
        parseFloat(plan.tax_amount) > 0 &&
        (plan.tax_paid === null || parseFloat(plan.tax_paid) === 0)
    );
}

export function needsPaymentMethod(plans) {
    // Active, not-paid-off agreement with no payment method on file
    return live(plans).some(plan =>
        plan.is_tpo &&
        plan.is_tpo_complete &&
        plan.active &&
        plan.payment_method_id === null &&
        (plan.paid === null || parseFloat(plan.paid) < parseFloat(plan.total))
    );
}
